import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import "../../assets/css/Steps.css";

function SubscriberList() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");

  const subscribersPerPage = 10;
  const token = localStorage.getItem("profileToken");
  const baseURL = process.env.REACT_APP_API_URL;

  // Fetch all subscribers
  useEffect(() => {
    const fetchSubscribers = async () => {
      try {
        const response = await axios.get(`${baseURL}/subscriber/all`, {
          headers: { "x-access-token": token || "" },
        });

        if (response.data.status) {
          setSubscribers(response.data.result || []);
        } else {
          setError(response.data.message || "Failed to fetch subscribers.");
        }
      } catch (err) {
        console.error("API Error:", err);
        setError("Failed to fetch subscribers.");
      } finally {
        setLoading(false);
      }
    };

    fetchSubscribers();
  }, [token, baseURL]);

  // DELETE API call
  const handleDelete = async (id) => {
    const subscriber = subscribers.find((sub) => sub.id === id);

    Swal.fire({
      title: `Delete ${subscriber?.email || "subscriber"}?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await axios.delete(`${baseURL}/subscriber/delete/${id}`, {
            headers: { "x-access-token": token || "" },
          });

          if (res.data.status) {
            setSubscribers((prev) => prev.filter((sub) => sub.id !== id));
            setSelected((prev) => prev.filter((s) => s !== id));
            Swal.fire("Deleted!", `${subscriber?.email || "Subscriber"} has been deleted.`, "success");
          } else {
            Swal.fire("Error", res.data.message || "Failed to delete subscriber.", "error");
          }
        } catch (err) {
          console.error("Delete Error:", err);
          Swal.fire("Error", "Something went wrong while deleting subscriber.", "error");
        }
      }
    });
  };

  const toggleSelect = (id) => {
    setSelected((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((s) => s !== id)
        : [...prevSelected, id]
    );
  };

  // Filter by search
  const filteredSubscribers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return subscribers.filter((sub) => {
      const email = (sub.email || "").toLowerCase();
      const idMatch = String(sub.id) === q;
      return q ? email.includes(q) || idMatch : true;
    });
  }, [subscribers, search]);

  // Pagination
  const totalPages = Math.ceil(filteredSubscribers.length / subscribersPerPage) || 1;
  const indexOfLast = currentPage * subscribersPerPage;
  const indexOfFirst = indexOfLast - subscribersPerPage;
  const currentSubscribers = filteredSubscribers.slice(indexOfFirst, indexOfLast);

  const paginate = (pageNum) => setCurrentPage(pageNum);

  // reset to page 1 on search
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Select All ONLY current page
  const toggleSelectAll = () => {
    const currentIds = currentSubscribers.map((sub) => sub.id);
    const allSelected =
      currentIds.length > 0 && currentIds.every((id) => selected.includes(id));

    if (allSelected) {
      setSelected((prev) => prev.filter((id) => !currentIds.includes(id)));
    } else {
      setSelected((prev) => [...new Set([...prev, ...currentIds])]);
    }
  };

  const handleSendMail = () => {
    if (selected.length === 0) {
      return Swal.fire("Error", "Select at least 1 subscriber", "error");
    }

    const emails = subscribers
      .filter((sub) => selected.includes(sub.id))
      .map((sub) => sub.email)
      .filter(Boolean);

    Swal.fire({
      title: "Send Mail",
      html: `Send mail to:<br/><b>${emails.join(", ")}</b>`,
      icon: "success",
    });
  };

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 p-3 border rounded shadow-sm bg-white">
        <div>
          <h4 className="mb-1 text-success">Newsletter Subscribers</h4>
          <div className="text-muted" style={{ fontSize: 14 }}>
            Total: <span className="badge bg-secondary ms-1">{filteredSubscribers.length}</span>
            <span className="ms-2 badge bg-dark">Selected: {selected.length}</span>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2">
          <button
            className="btn btn-success rounded-pill px-4"
            onClick={handleSendMail}
            disabled={selected.length === 0}
          >
            Send Mail
          </button>

          <button className="btn btn-secondary rounded-pill px-4" onClick={toggleSelectAll}>
            {currentSubscribers.length > 0 &&
            currentSubscribers.every((sub) => selected.includes(sub.id))
              ? "Deselect All"
              : "Select All"}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by email or ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="text-center my-5">Loading subscribers...</div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : filteredSubscribers.length === 0 ? (
        <div className="alert alert-info">No subscribers found</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-bordered table-hover align-middle">
              <thead className="table-success">
                <tr>
                  <th style={{ width: 80 }}>Select</th>
                  <th style={{ width: 80 }}>#</th>
                  <th>Email</th>
                  <th>Subscribed At</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentSubscribers.map((subscriber, index) => (
                  <tr key={subscriber.id}>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        checked={selected.includes(subscriber.id)}
                        onChange={() => toggleSelect(subscriber.id)}
                      />
                    </td>

                    <td>{indexOfFirst + index + 1}</td>
                    <td>{subscriber.email}</td>
                    <td>{new Date(subscriber.created_at).toLocaleString()}</td>

                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(subscriber.id)}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav>
              <ul className="pagination">
                {Array.from({ length: totalPages }, (_, i) => (
                  <li
                    key={i + 1}
                    className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                  >
                    <button className="page-link" onClick={() => paginate(i + 1)}>
                      {i + 1}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

export default SubscriberList;
